import SparkMD5 from 'spark-md5';
import { api } from '../../../general/apiScenarios';
import { dropLicenseIds, getLicenseIds } from '../../../general/fontLicense/licenseRegistry';
import { isNonRetryableUploadCode, uploadErrorMessage } from '../../../general/fontLicense/model';
import * as uploaderActions from './actions';
import { getFile } from './fileRegistry';
import { Job, JobStatus } from './model';
import { scenarioFactoryAsync } from '../../../general/scenarioFactoryAsync';
import { _take, ListenerApiLight } from '../../listenerMiddleware';
import { sum } from 'lodash';
import Notifications from 'react-notification-system-redux';
import { INode } from '../../../general/model';
import { errorTextFromResponse } from '../../../general/utils/apiErrorText';
import { computeFileMd5 } from '../../../general/utils/computeFileMd5';
import { resolve } from '../conflictResolver/sagas';
import { AutoConflictResolution } from '../conflictResolver/model';

const CHUNK_SIZE = 150 * 1024 * 1024; // 150MiB
const MAX_UPLOAD_RETRIES = 5; // Max retry count
const RETRY_DELAY_MS = 2000; // Delay between retries

class UploadError extends Error {
  constructor(public message: string) {
    super(message);
  }
}

export const uploadScenario = scenarioFactoryAsync(
  uploadSingleFile,
  async (lapi: ListenerApiLight, job: Job) => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const action = await _take(lapi, uploaderActions.cancelAllJobs, uploaderActions.groupDeleted);
      lapi.signal.throwIfAborted();
      if (
        action.type === 'creativeHub/uploader/cancelAllJobs' ||
        action.payload.groupID === job.groupID
      ) {
        return;
      }
    }
  },
  null,
  'uploadSingleFile',
);
// A name-conflict reported by create_multipart_upload is ALWAYS HTTP 400 with a
// structured body: { code: "invalid", data: { message: "...already exists.",
// size, created_at } } (nod.py DoubleValidationError). Other 400s on this endpoint
// (invalid filename, serializer errors) return `data` as a plain string. So detect
// a conflict by the body signature, not by "any 400": otherwise we would show the
// overwrite dialog for unrelated validation errors.
function isNameConflict(result: {
  errorData?: { status?: number | null; response?: any };
}): boolean {
  if (result.errorData?.status !== 400) return false;
  const data = result.errorData?.response?.data;
  if (data === null || typeof data !== 'object') return false;
  const conflict = data as { message?: unknown; size?: unknown; created_at?: unknown };
  const messageHasConflict =
    typeof conflict.message === 'string' && conflict.message.includes('already exists');
  const hasConflictFields = conflict.size !== undefined || conflict.created_at !== undefined;
  return messageHasConflict || hasConflictFields;
}

/**
 * @returns the created file node on success, or `null` when the job was skipped
 * by the user on a name conflict (in which case the SKIPPED status is already
 * dispatched). Throws on genuine failure.
 */
async function uploadSingleFile(lapi: ListenerApiLight, job: Job): Promise<INode | null> {
  // The actual File object lives in the module-level fileRegistry (MYUA-16384),
  // keyed by the job uuid — the Redux store only keeps serializable metadata.
  const file = getFile(job.uuid);
  if (!file) {
    // File reference is gone (e.g. registry cleared). The upload cannot proceed;
    // fail the job with a clear message instead of throwing an opaque error.
    lapi.dispatch(
      uploaderActions.jobUpdated({
        jobID: job.uuid,
        jobProps: { status: JobStatus.UPLOAD_FAILURE, errorMessage: 'File reference lost' },
      }),
    );
    lapi.dispatch(
      Notifications.error({
        uid: job.uuid,
        title: 'File reference lost',
        message: 'Please re-add the file and upload again.',
      }),
    );
    return null;
  }

  // Ids of the FontLicense records created from the license form, keyed by the File
  // reference (bound via bindLicenseIdsToFiles). An archive carries the ids of every
  // font found inside it on the client — the backend does not look into archives.
  const fontLicenseIds = getLicenseIds(file);

  //small files are uploaded at once
  if (file.size <= CHUNK_SIZE) {
    const node = await uploadWholeFileAtOnce(lapi, job, file, fontLicenseIds);
    dropLicenseIds(file);
    return node;
  }

  //big files are uploaded in parts
  // Pre-hash the whole file (streaming, slice-by-slice — no full-file ArrayBuffer)
  // because the new create_multipart_upload contract needs md5_of_file up-front;
  // the backend verifies it when the last part arrives.
  const md5OfFile = await computeFileMd5(file, CHUNK_SIZE);
  lapi.signal.throwIfAborted();

  let overwrite = job.overwrite;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const uploadID = await api.fs.getUploadID(
      file,
      job.parent_node_id,
      CHUNK_SIZE,
      md5OfFile,
      overwrite,
      fontLicenseIds,
    );
    lapi.signal.throwIfAborted();

    if (uploadID.success) {
      const createdNode = await uploadByPart(lapi, file, uploadID.data.response.id, job.uuid);
      lapi.signal.throwIfAborted();
      dropLicenseIds(file);
      return createdNode;
    }

    // Defensive name-conflict handling: checkExistantFiles already resolves
    // conflicts before enqueue, but a file may appear between the check and the
    // upload. If the backend reports a conflict and we are not overwriting yet,
    // ask the user.
    if (isNameConflict(uploadID) && !overwrite) {
      const decision = await resolve(lapi, file.name);
      lapi.signal.throwIfAborted();
      if (decision === AutoConflictResolution.Overwrite) {
        overwrite = true;
        continue;
      }
      // Skip: leave the job as SKIPPED and signal "no node created".
      lapi.dispatch(
        uploaderActions.jobUpdated({
          jobID: job.uuid,
          jobProps: { status: JobStatus.SKIPPED },
        }),
      );
      return null;
    }

    // On non-conflict 400 the backend sends `data` as a plain string; on network
    // failures response/data may be null. errorTextFromResponse normalises both.
    const response = uploadID.errorData?.response;
    const fallback = errorTextFromResponse(response, 'Unknown error while uploading file');
    // create_multipart_upload validates the font license too, so map its codes to
    // the same wording the single-part path uses — otherwise a rejected license
    // showed up here as raw backend English.
    const isLicenseError = isNonRetryableUploadCode(response?.code);
    const title = isLicenseError ? uploadErrorMessage(response?.code, fallback) : fallback;
    // Persist the reason on the job: the toast auto-dismisses, the job row does not.
    lapi.dispatch(
      uploaderActions.jobUpdated({
        jobID: job.uuid,
        jobProps: { errorMessage: title },
      }),
    );
    lapi.dispatch(
      Notifications.error({
        title,
        message: isLicenseError
          ? undefined
          : 'Try another browser. Contact your administrator or support chat if the problem still persists.',
      }),
    );
    throw uploadID.reason;
  }
}

async function uploadWholeFileAtOnce(
  lapi: ListenerApiLight,
  job: Job,
  file: File,
  fontLicenseIds?: number[],
): Promise<INode> {
  console.debug('uploadSingleFile: sending a file...');
  let attempts = 0;
  const startTime = Date.now();
  while (!lapi.signal.aborted && attempts < MAX_UPLOAD_RETRIES) {
    const linreg = new TimeWindowedLinearRegression();
    console.debug('uploadSingleFile: making a request...');
    let result: {
      response: any;
      status: number;
      success: boolean;
    };

    try {
      result = await api.fs.uploadFile(
        file,
        job.parent_node_id,
        lapi.signal,
        (loaded, total) => {
          const { remainingTime, uploadSpeed } = linreg.updateRemainingTime(file.size - loaded);
          lapi.dispatch(
            uploaderActions.jobUpdated({
              jobID: job.uuid,
              jobProps: {
                uploadProgress: loaded / total,
                remainingTime,
                ellapsedTime: Date.now() - startTime,
                uploadSpeed,
              },
            }),
          );
        },
        true, // overwrite
        fontLicenseIds,
      );
      lapi.signal.throwIfAborted();
      console.debug('uploadSingleFile: returned success?', result.success);
      console.debug('uploadSingleFile: signal aborted?', lapi.signal.aborted);
      if (result.success) {
        return result.response.node;
      }
      // lapi.dispatch(
      //   Notifications.error({
      //     title: ,
      //   }),
      // );
      // `data` is an array of strings for DRF validation errors but a plain string
      // (or null) elsewhere — going through errorTextFromResponse keeps a non-array
      // body from throwing a TypeError that would mask the real reason.
      const errorMessage = errorTextFromResponse(
        result.response,
        result.response == null
          ? 'Server response is undefined or null'
          : 'Unknown error while uploading file',
      );

      // License/validation errors are deterministic — retrying just wastes 5
      // attempts and then hides the real cause. Throw an UploadError (non-retryable
      // branch) with a clear reason shown via Notifications.error below.
      if (isNonRetryableUploadCode(result.response?.code)) {
        throw new UploadError(uploadErrorMessage(result.response?.code, errorMessage));
      } else {
        throw new Error(errorMessage);
      }
    } catch (x) {
      console.log(x);

      // Deterministic rejection (font license, validation). Checked BEFORE the
      // max-retries branch, which would otherwise overwrite the real reason with
      // "failed after maximum retries" when it lands on the last attempt. The
      // reason is persisted on the job as well as toasted: the toast auto-dismisses,
      // the job row stays, and without errorMessage that row only says
      // "Upload failed".
      if (x instanceof UploadError) {
        lapi.dispatch(
          uploaderActions.jobUpdated({
            jobID: job.uuid,
            jobProps: { status: JobStatus.UPLOAD_FAILURE, errorMessage: x.message },
          }),
        );
        lapi.dispatch(
          Notifications.error({
            title: x.message,
          }),
        );
        throw x;
      }

      attempts++;
      if (attempts >= MAX_UPLOAD_RETRIES) {
        // set failure status after max retries
        const reason = x instanceof Error ? x.message : 'Unknown error while uploading file';
        lapi.dispatch(
          uploaderActions.jobUpdated({
            jobID: job.uuid,
            jobProps: { status: JobStatus.UPLOAD_FAILURE, errorMessage: reason },
          }),
        );

        lapi.dispatch(
          Notifications.error({
            title: 'File upload failed after maximum retries',
            message: reason,
          }),
        );

        throw x;
      }
      if (lapi.signal.aborted) {
        //nothing
      } else {
        lapi.dispatch(
          Notifications.error({
            uid: job.uuid,
            title: x instanceof Error ? x.message : 'Unknown error while uploading file',
            message:
              'Try another browser. Contact your administrator or support chat if the problem still persists.',
          }),
        );
      }

      // wait before new attempt if we had an error
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    }
  }
  throw new Error('File upload failed after maximum retries');
}

/**
 * Uploads the file part-by-part. The whole-file MD5 is NOT computed here anymore
 * (it is pre-hashed once and sent to create_multipart_upload).
 *
 * The backend creates the file node only on the last part:
 *  - intermediate parts -> 202 (empty body, response === null)
 *  - last part          -> 201 with the node as a FLAT object in the response root
 *    (NodeSerializer(node).data + `perms`), i.e. the node fields are at the top
 *    level of `result.response`, NOT wrapped in `{ node }`.
 * Since `sendfile` treats any 2xx as success without distinguishing statuses,
 * the caller must inspect `result.status` explicitly. The last part is only
 * considered done when status === 201 AND the response carries a node (has `id`);
 * otherwise the file is incomplete and the upload fails.
 *
 * @returns the created file node from the last part's 201 response.
 */
async function uploadByPart(
  lapi: ListenerApiLight,
  file: File,
  uploadID: number,
  jobUUID: string,
): Promise<INode> {
  const linreg = new TimeWindowedLinearRegression();
  const totalParts = Math.ceil(file.size / CHUNK_SIZE);
  const startTime = Date.now();
  let createdNode: INode | null = null;

  const failJob = () => {
    lapi.dispatch(
      uploaderActions.jobUpdated({
        jobID: jobUUID,
        jobProps: { status: JobStatus.UPLOAD_FAILURE },
      }),
    );
  };

  for (let currentPart = 1; currentPart <= totalParts; currentPart++) {
    const blobData = await getBlob(file, currentPart);

    const isLastPart = currentPart === totalParts;
    let attempts = 0;
    let partSucceeded = false;
    const currentlyUploadedBytes = CHUNK_SIZE * (currentPart - 1);

    while (!lapi.signal.aborted && attempts < MAX_UPLOAD_RETRIES) {
      console.debug('uploadByPart: sending a part...');
      let result: Awaited<ReturnType<typeof api.fs.uploadFilePart>> | undefined = undefined;
      try {
        result = await api.fs.uploadFilePart(
          currentPart,
          totalParts,
          blobData,
          uploadID,
          lapi.signal,
          (loaded) => {
            const totalLoaded = currentlyUploadedBytes + loaded;
            const { remainingTime, uploadSpeed } = linreg.updateRemainingTime(
              file.size - totalLoaded,
            );
            lapi.dispatch(
              uploaderActions.jobUpdated({
                jobID: jobUUID,
                jobProps: {
                  uploadProgress: totalLoaded / file.size,
                  uploadSpeed,
                  remainingTime,
                  ellapsedTime: Date.now() - startTime,
                },
              }),
            );
          },
        );

        lapi.signal.throwIfAborted();

        if (result?.success) {
          if (isLastPart) {
            // File node is created only on 201; the node is a FLAT object in the
            // response root (has `id`). Anything else (e.g. 202) or a missing node
            // means the file is incomplete -> retry.
            if (result.status === 201 && result.response && result.response.id) {
              createdNode = result.response;
              partSucceeded = true;
              break;
            }
            console.warn(
              `uploadByPart: last part returned status ${result.status} without a node; retrying`,
            );
          } else {
            if (result.status !== 202) {
              console.warn(
                `uploadByPart: unexpected status ${result.status} for intermediate part ` +
                  `${currentPart}/${totalParts}`,
              );
            }
            partSucceeded = true;
            break;
          }
        }

        attempts++;
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      } catch (x) {
        attempts++;

        if (attempts >= MAX_UPLOAD_RETRIES) {
          failJob();
          throw x;
        }

        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      }
    }

    if (!partSucceeded) {
      lapi.signal.throwIfAborted();
      failJob();
      throw new UploadError(`Failed to upload part ${currentPart}/${totalParts}`);
    }
  }

  if (!createdNode) {
    lapi.signal.throwIfAborted();
    failJob();
    throw new UploadError('Multipart upload finished without a created node');
  }

  return createdNode;
}

function getStartEndOfPart(size: number, currentPart: number) {
  const start = (currentPart - 1) * CHUNK_SIZE;
  const end = Math.min(start + CHUNK_SIZE, size);
  return [start, end];
}

/**
 *
 * @param file файл, который препарируем
 * @param currentPart часть файла
 * @returns arraybuffer: кусок файла, md5: его хеш
 */
export async function getBlob(
  file: Blob,
  currentPart?: number,
): Promise<{ md5: string; arrayBuffer: ArrayBuffer }> {
  let blob = file;
  if (currentPart !== undefined) {
    const [start, end] = getStartEndOfPart(file.size, currentPart);
    blob = file.slice(start, end);
  }

  const arrayBuffer = await blob.arrayBuffer();
  const spark = new SparkMD5.ArrayBuffer();
  spark.append(arrayBuffer);
  const md5 = spark.end();
  // const hash = await window.crypto.subtle.digest("SHA-256", ab);

  return { md5, arrayBuffer };
}

/**
 * @see https://en.wikipedia.org/wiki/Simple_linear_regression#Formulation_and_computation
 */
class TimeWindowedLinearRegression {
  public records: [number, number, number][] = [];
  constructor(private window?: number) {}

  public updateRemainingTime(remainingBytes: number) {
    const now = Date.now();
    this.append(remainingBytes, now);
    const { slope, yIntercept } = this.calc();
    return {
      remainingTime: yIntercept - now,
      uploadSpeed: slope !== 0 ? Math.abs(1000 / slope) : 0,
    };
  }

  private cleanup() {
    const { window } = this;
    if (window === undefined) return;
    const now = new Date().valueOf();
    const index = this.records.findIndex((record) => now - record[2] < window);
    this.records = this.records.slice(index);
  }
  public append(x: number, y: number) {
    this.cleanup();
    this.records.push([x, y, new Date().valueOf()]);
  }
  public calc() {
    this.cleanup();
    const length = this.records.length;
    if (length === 0) return { slope: NaN, yIntercept: NaN };

    const sumX = sum(this.records.map((r) => r[0]));
    const sumY = sum(this.records.map((r) => r[1]));
    const meanX = sumX / length;
    const meanY = sumY / length;

    // 𝚺 Δx_i×Δy_i
    const dxdySum = sum(this.records.map(([x, y]) => (x - meanX) * (y - meanY)));
    // 𝚺 Δx_i^2
    const dxSqSum = sum(this.records.map(([x]) => (x - meanX) ** 2));

    const slope = dxdySum / dxSqSum;
    const yIntercept = meanY - slope * meanX;

    return { slope, yIntercept };
  }
}
